package kz.dentalcrm.app.screens.appointments

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.not_visited
import dentalcrm.composeapp.generated.resources.patient_status
import dentalcrm.composeapp.generated.resources.pending
import dentalcrm.composeapp.generated.resources.save
import dentalcrm.composeapp.generated.resources.visited
import io.github.alexzhirkevich.compottie.LottieCompositionSpec
import io.github.alexzhirkevich.compottie.animateLottieCompositionAsState
import io.github.alexzhirkevich.compottie.rememberLottieComposition
import io.github.alexzhirkevich.compottie.rememberLottiePainter
import kotlinx.collections.immutable.persistentListOf
import kz.dentalcrm.app.core.ui.components.StyledSegmentedControl
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource

private fun statusToIndex(status: String): Int = when (status) {
    "client_miss" -> 0
    "pending" -> 1
    "success" -> 2
    else -> 1
}

private fun indexToStatus(index: Int): String = when (index) {
    0 -> "client_miss"
    1 -> "pending"
    2 -> "success"
    else -> "pending"
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AppointmentStatusDialog(
    status: String = "pending",
    onDismiss: () -> Unit,
    onStatusChanged: (status: String) -> Unit,
) {
    val modalBottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val segments = persistentListOf(
        stringResource(Res.string.not_visited),
        stringResource(Res.string.pending),
        stringResource(Res.string.visited)
    )
    val initialIndex = statusToIndex(status)
    var selectedSegment by rememberSaveable { mutableIntStateOf(initialIndex) }
    val pagerState = rememberPagerState(initialPage = initialIndex) { segments.size }

    val missingComposition by rememberLottieComposition {
        LottieCompositionSpec.JsonString(
            Res.readBytes("files/missing.json").decodeToString()
        )
    }
    val pendingComposition by rememberLottieComposition {
        LottieCompositionSpec.JsonString(
            Res.readBytes("files/pending.json").decodeToString()
        )
    }
    val successComposition by rememberLottieComposition {
        LottieCompositionSpec.JsonString(
            Res.readBytes("files/success.json").decodeToString()
        )
    }

    var playingPage by remember { mutableIntStateOf(selectedSegment) }

    LaunchedEffect(selectedSegment) {
        pagerState.animateScrollToPage(
            page = selectedSegment,
            animationSpec = tween(durationMillis = 300)
        )
        playingPage = selectedSegment
    }

    ModalBottomSheet(
        sheetState = modalBottomSheetState,
        onDismissRequest = onDismiss,
        containerColor = DefaultTheme.colors.surfaceSecondary
    ) {
        Column(
            modifier = Modifier.fillMaxWidth()
        ) {
            Text(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp),
                style = DefaultTheme.typography.subHeading1,
                text = stringResource(Res.string.patient_status),
                textAlign = TextAlign.Center,
                color = DefaultTheme.colors.onSurface
            )

            Spacer(modifier = Modifier.height(16.dp))

            HorizontalPager(
                state = pagerState,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(160.dp),
                userScrollEnabled = false
            ) { page ->
                val isCurrentPage = pagerState.currentPage == page
                val shouldPlay = playingPage == page && isCurrentPage

                // Scale animation
                val scale by animateFloatAsState(
                    targetValue = if (isCurrentPage) 1f else 0.8f,
                    animationSpec = tween(durationMillis = 300),
                    label = "scale_animation"
                )
                val alpha by animateFloatAsState(
                    targetValue = if (isCurrentPage) 1f else 0f,
                    animationSpec = tween(durationMillis = 300),
                    label = "alpha_animation"
                )

                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .graphicsLayer {
                            scaleX = scale
                            scaleY = scale
                            this.alpha = alpha
                        },
                    contentAlignment = Alignment.Center
                ) {
                    val composition = when (page) {
                        0 -> missingComposition
                        1 -> pendingComposition
                        2 -> successComposition
                        else -> null
                    }

                    val progress by animateLottieCompositionAsState(
                        composition = composition,
                        isPlaying = shouldPlay,
                        restartOnPlay = true,
                        iterations = 1
                    )

                    Image(
                        modifier = Modifier.size(140.dp),
                        painter = rememberLottiePainter(
                            composition = composition,
                            progress = { progress }
                        ),
                        contentDescription = "Status animation"
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp)
            ) {
                StyledSegmentedControl(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(64.dp),
                    segments = segments,
                    selectedSegment = segments[selectedSegment],
                    cornerRadius = 16.dp,
                    backgroundColor = DefaultTheme.colors.surfaceAlternative,
                    onSegmentSelected = { item ->
                        val index = segments.indexOf(item)
                        selectedSegment = index
                    }
                ) { titleId, selected ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center
                    ) {
                        Text(
                            text = titleId,
                            color = if (selected) DefaultTheme.colors.onPrimary else DefaultTheme.colors.onSurface,
                            style = DefaultTheme.typography.labelMedium
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            UIButton(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp),
                text = stringResource(Res.string.save),
                onClick = {
                    onStatusChanged(indexToStatus(selectedSegment))
                    onDismiss()
                }
            )

            Box(
                modifier = Modifier
                    .size(16.dp)
                    .statusBarsPadding()
            )
        }
    }
}
